import { z } from 'zod';

import type { SaveShipmentRequest, SaveShipmentResponse } from './save-shipment.dto.js';
import {
  ShipmentHeaderSchema,
  ShipmentRangeBuilderInputSchema,
  ShipmentReceiverSelectionSchema,
  ShipmentSourceEvidenceSchema,
  ShipmentTargetBuilderInputSchema,
  ShipmentTotalsSummarySchema,
} from './shipments-shared.schema.js';

const saveShipmentBaseShape = {
  companyId: z.uuid(),
  stationId: z.uuid(),
  shipmentId: z.uuid().optional(),
  receiver: ShipmentReceiverSelectionSchema,
  ...ShipmentSourceEvidenceSchema.shape,
};

const SaveShipmentRangeSchema = ShipmentRangeBuilderInputSchema.extend(
  saveShipmentBaseShape,
).strict();

const SaveShipmentTargetSchema = ShipmentTargetBuilderInputSchema.extend(
  saveShipmentBaseShape,
).strict();

export const SaveShipmentRequestSchema: z.ZodType<SaveShipmentRequest> = z
  .discriminatedUnion('mode', [SaveShipmentRangeSchema, SaveShipmentTargetSchema])
  .superRefine((value, ctx) => {
    const rangeStartMs = Date.parse(value.rangeStartAt);
    const rangeEndMs = Date.parse(value.rangeEndAt);
    if (!Number.isNaN(rangeStartMs) && !Number.isNaN(rangeEndMs) && rangeStartMs >= rangeEndMs) {
      ctx.addIssue({
        code: 'custom',
        message: 'rangeStartAt must be before rangeEndAt.',
        path: ['rangeEndAt'],
      });
    }
    const hasLoadSessionId = value.sourceLoadSessionId !== undefined && value.sourceLoadSessionId !== null;
    const hasGroupId =
      value.sourceLoadSessionGroupId !== undefined && value.sourceLoadSessionGroupId !== null;
    if (hasLoadSessionId !== hasGroupId) {
      ctx.addIssue({
        code: 'custom',
        message: 'sourceLoadSessionId and sourceLoadSessionGroupId must be provided together.',
        path: [hasLoadSessionId ? 'sourceLoadSessionGroupId' : 'sourceLoadSessionId'],
      });
    }
  });

export const SaveShipmentResponseSchema: z.ZodType<SaveShipmentResponse> = z
  .object({
    shipment: ShipmentHeaderSchema,
    lineItemIds: z.array(z.uuid()),
    purchaseCommittedIds: z.array(z.uuid()),
    totals: ShipmentTotalsSummarySchema,
  })
  .strict();
